import { useEffect, useState } from 'react';
import { Calendar } from 'lucide-react';

import { PREFERENCE_FEEDBACK_MESSAGES } from '@shared/guidedPlanningPreferences';
import { cn } from '@client/lib';

import { useSession, useIsSessionComplete } from '@/features/guided-financial-planning/context';
import {
  usePreferenceQuestionProgress,
  usePreferenceStepHandlers,
} from '@/features/guided-financial-planning/hooks';
import { StepLayout } from '@/features/guided-financial-planning/components/step-layout';
import { PreferenceFeedback } from '@/features/guided-financial-planning/components/preference-feedback';
import {
  RadioOptionCards,
  type RadioOption,
} from '@/features/guided-financial-planning/components/radio-option-cards';

const PAYMENT_PREFERENCE_OPTIONS: RadioOption<PaymentPreference>[] = [
  {
    value: 'upfront',
    title: 'Pay Everything Upfront',
    description: 'One-time lump sum payment',
  },
  {
    value: 'monthly',
    title: 'Small Monthly Premiums',
    description: 'Lower payments spread over time',
  },
  {
    value: 'balanced',
    title: 'Somewhere in the Middle',
    description: 'Balanced payment approach',
  },
  {
    value: 'no-preference',
    title: "I Don't Have a Preference",
    description: 'Show me all options',
  },
];

export function PreferencesPaymentStep() {
  const { session } = useSession();
  const isSessionComplete = useIsSessionComplete();
  const { questionNumber, questionTotal, activeDotIndex } =
    usePreferenceQuestionProgress('preferences-payment');

  const sessionValue = session?.stepData?.preferences?.paymentPreference;
  const [paymentPreference, setPaymentPreference] = useState<PaymentPreference | undefined>(
    sessionValue,
  );

  useEffect(() => {
    if (sessionValue && !paymentPreference) {
      setPaymentPreference(sessionValue);
    }
  }, [sessionValue, paymentPreference]);

  const { handleNext, handleBack, isSaving } = usePreferenceStepHandlers({
    preferenceKey: 'paymentPreference',
    value: paymentPreference,
  });

  const isValid = paymentPreference !== undefined;

  const feedbackMessage = paymentPreference
    ? PREFERENCE_FEEDBACK_MESSAGES.paymentPreference[paymentPreference]
    : null;

  return (
    <StepLayout
      title="How do you prefer to pay?"
      subtitle="Choose the payment structure that works best for your financial situation."
      icon={
        <div className="flex flex-col items-center gap-3">
          <span className="text-sm font-medium text-gray-500">
            Question {questionNumber} of {questionTotal}
          </span>
          <div className="bg-primary/10 flex size-14 items-center justify-center rounded-full">
            <Calendar className="text-primary size-7" />
          </div>
        </div>
      }
      nextButtonLabel="Continue"
      isNextDisabled={!isValid || isSaving}
      onNext={handleNext}
      onBack={handleBack}
      isLoading={isSaving}
    >
      <div className="flex flex-col items-center">
        <RadioOptionCards
          options={PAYMENT_PREFERENCE_OPTIONS}
          value={paymentPreference}
          onChange={setPaymentPreference}
          className="w-full max-w-lg"
          disabled={isSessionComplete}
        />
        <div className="w-full max-w-lg">
          <PreferenceFeedback message={feedbackMessage} />
        </div>
        <div className="mt-8 flex justify-center gap-2">
          {Array.from({ length: questionTotal }, (_, i) => (
            <div
              key={i}
              className={cn('size-2 rounded-full', i === activeDotIndex ? 'bg-primary' : 'bg-gray-300')}
            />
          ))}
        </div>
      </div>
    </StepLayout>
  );
}
